import React, { useEffect } from 'react';
import HelpLayout from '../../../components/help/HelpLayout';

const UserAdministrationHelp = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const breadcrumbs = [
    { name: 'Admin Guide', href: '/help/admin/dashboard' },
    { name: 'User Administration' }
  ];

  return (
    <HelpLayout title="User Administration" breadcrumbs={breadcrumbs}>
      <div className="help-content">
        <div className="card">
          <div className="card-body">
            <h2 style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: 'var(--primary-black)',
              marginBottom: '16px'
            }}>
              User Administration Guide
            </h2>
            <p style={{
              fontSize: '16px',
              color: 'var(--text-gray)',
              lineHeight: 1.6
            }}>
              Manage user accounts, roles, permissions, and account verification processes.
            </p>

            {/* User Management Overview */}
            <div style={{ marginTop: '32px' }}>
              <h3 style={{
                fontSize: '20px',
                fontWeight: 'bold',
                color: 'var(--primary-black)',
                marginBottom: '16px'
              }}>
                👥 User Account Management
              </h3>

              <div style={{ marginBottom: '24px' }}>
                <h4 style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: 'var(--primary-black)',
                  marginBottom: '12px'
                }}>
                  Accessing User Management
                </h4>
                <p style={{ color: 'var(--text-gray)', lineHeight: 1.6, marginBottom: '12px' }}>
                  User administration is handled through the Django Admin interface:
                </p>
                <ol style={{
                  color: 'var(--text-gray)',
                  lineHeight: 1.6,
                  paddingLeft: '20px',
                  marginBottom: '16px'
                }}>
                  <li>Navigate to <strong>/admin/</strong> from the main application</li>
                  <li>Log in with your administrator credentials</li>
                  <li>Access <strong>Users → Users</strong> for user account management</li>
                  <li>Use <strong>Authentication and Authorization</strong> for permissions</li>
                </ol>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <h4 style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: 'var(--primary-black)',
                  marginBottom: '12px'
                }}>
                  User Account Types
                </h4>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                  gap: '16px',
                  marginBottom: '16px'
                }}>
                  <div style={{
                    padding: '16px',
                    backgroundColor: '#e0f2fe',
                    borderRadius: '6px',
                    border: '1px solid #0284c7'
                  }}>
                    <h5 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#0284c7',
                      marginBottom: '8px'
                    }}>
                      👤 Regular Users (Learners)
                    </h5>
                    <p style={{ fontSize: '14px', color: '#0284c7', margin: 0 }}>
                      Standard learner accounts with course browsing and application capabilities
                    </p>
                  </div>
                  <div style={{
                    padding: '16px',
                    backgroundColor: '#fef3c7',
                    borderRadius: '6px',
                    border: '1px solid #f59e0b'
                  }}>
                    <h5 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#d97706',
                      marginBottom: '8px'
                    }}>
                      👨‍💼 Staff Users
                    </h5>
                    <p style={{ fontSize: '14px', color: '#d97706', margin: 0 }}>
                      GCRA staff with administrative access to review applications and manage courses
                    </p>
                  </div>
                  <div style={{
                    padding: '16px',
                    backgroundColor: '#dcfce7',
                    borderRadius: '6px',
                    border: '1px solid #16a34a'
                  }}>
                    <h5 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#16a34a',
                      marginBottom: '8px'
                    }}>
                      🛡️ Superusers
                    </h5>
                    <p style={{ fontSize: '14px', color: '#16a34a', margin: 0 }}>
                      Full system administrators with complete access to all features and data
                    </p>
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <h4 style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: 'var(--primary-black)',
                  marginBottom: '12px'
                }}>
                  Key User Information Fields
                </h4>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                  gap: '16px',
                  marginBottom: '16px'
                }}>
                  <div style={{
                    padding: '16px',
                    backgroundColor: 'var(--background-gray)',
                    borderRadius: '6px'
                  }}>
                    <h5 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: 'var(--primary-black)',
                      marginBottom: '8px'
                    }}>
                      📧 Contact Information
                    </h5>
                    <ul style={{ fontSize: '14px', color: 'var(--text-gray)', margin: 0, paddingLeft: '16px' }}>
                      <li>Email address (unique)</li>
                      <li>Phone number</li>
                      <li>Physical address</li>
                      <li>Postal code</li>
                    </ul>
                  </div>
                  <div style={{
                    padding: '16px',
                    backgroundColor: 'var(--background-gray)',
                    borderRadius: '6px'
                  }}>
                    <h5 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: 'var(--primary-black)',
                      marginBottom: '8px'
                    }}>
                      🆔 Personal Details
                    </h5>
                    <ul style={{ fontSize: '14px', color: 'var(--text-gray)', margin: 0, paddingLeft: '16px' }}>
                      <li>South African ID number</li>
                      <li>Date of birth</li>
                      <li>Gender and race</li>
                      <li>Province and city</li>
                    </ul>
                  </div>
                  <div style={{
                    padding: '16px',
                    backgroundColor: 'var(--background-gray)',
                    borderRadius: '6px'
                  }}>
                    <h5 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: 'var(--primary-black)',
                      marginBottom: '8px'
                    }}>
                      🎓 Education Background
                    </h5>
                    <ul style={{ fontSize: '14px', color: 'var(--text-gray)', margin: 0, paddingLeft: '16px' }}>
                      <li>Highest grade completed</li>
                      <li>Matric status and year</li>
                      <li>Tertiary qualifications</li>
                      <li>Institution details</li>
                    </ul>
                  </div>
                  <div style={{
                    padding: '16px',
                    backgroundColor: 'var(--background-gray)',
                    borderRadius: '6px'
                  }}>
                    <h5 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: 'var(--primary-black)',
                      marginBottom: '8px'
                    }}>
                      📍 Location Data
                    </h5>
                    <ul style={{ fontSize: '14px', color: 'var(--text-gray)', margin: 0, paddingLeft: '16px' }}>
                      <li>GPS coordinates</li>
                      <li>Economic corridor</li>
                      <li>Address verification</li>
                      <li>Location sharing consent</li>
                    </ul>
                  </div>
                </div>
              </div>
            {/* Account Verification */}
            <div style={{ marginTop: '32px' }}>
              <h3 style={{
                fontSize: '20px',
                fontWeight: 'bold',
                color: 'var(--primary-black)',
                marginBottom: '16px'
              }}>
                ✅ Account Verification Process
              </h3>

              <div style={{ marginBottom: '24px' }}>
                <h4 style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: 'var(--primary-black)',
                  marginBottom: '12px'
                }}>
                  Profile Completion Verification
                </h4>
                <p style={{ color: 'var(--text-gray)', lineHeight: 1.6, marginBottom: '12px' }}>
                  Ensure user profiles meet minimum requirements for course applications:
                </p>
                <ul style={{
                  color: 'var(--text-gray)',
                  lineHeight: 1.6,
                  paddingLeft: '20px',
                  marginBottom: '16px'
                }}>
                  <li><strong>Personal Information:</strong> Complete name, contact details, and demographics</li>
                  <li><strong>ID Verification:</strong> Valid South African ID number with auto-filled details</li>
                  <li><strong>Education Records:</strong> Minimum education level and qualifications</li>
                  <li><strong>Location Data:</strong> Address and economic corridor assignment</li>
                  <li><strong>Profile Picture:</strong> Optional but recommended for identification</li>
                </ul>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <h4 style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: 'var(--primary-black)',
                  marginBottom: '12px'
                }}>
                  ID Number Validation
                </h4>
                <div style={{
                  padding: '16px',
                  backgroundColor: '#fef3c7',
                  borderRadius: '6px',
                  border: '1px solid #f59e0b',
                  marginBottom: '16px'
                }}>
                  <h5 style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#d97706',
                    marginBottom: '8px'
                  }}>
                    🔍 Automatic Validation Features
                  </h5>
                  <ul style={{ fontSize: '14px', color: '#d97706', margin: 0, paddingLeft: '16px' }}>
                    <li>Format validation for 13-digit SA ID numbers</li>
                    <li>Automatic date of birth extraction</li>
                    <li>Gender determination from ID number</li>
                    <li>Duplicate ID prevention across all users</li>
                  </ul>
                </div>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <h4 style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: 'var(--primary-black)',
                  marginBottom: '12px'
                }}>
                  Account Status Management
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{
                    padding: '16px',
                    backgroundColor: '#dcfce7',
                    borderRadius: '6px',
                    borderLeft: '4px solid #16a34a'
                  }}>
                    <h5 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#16a34a',
                      marginBottom: '4px'
                    }}>
                      ✅ Active Accounts
                    </h5>
                    <p style={{ fontSize: '14px', color: '#16a34a', margin: 0 }}>
                      Full access to platform features, can apply for courses
                    </p>
                  </div>

                  <div style={{
                    padding: '16px',
                    backgroundColor: '#fee2e2',
                    borderRadius: '6px',
                    borderLeft: '4px solid #ef4444'
                  }}>
                    <h5 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#dc2626',
                      marginBottom: '4px'
                    }}>
                      🚫 Inactive Accounts
                    </h5>
                    <p style={{ fontSize: '14px', color: '#dc2626', margin: 0 }}>
                      Login disabled, cannot access platform features
                    </p>
                  </div>

                  <div style={{
                    padding: '16px',
                    backgroundColor: '#fef3c7',
                    borderRadius: '6px',
                    borderLeft: '4px solid #f59e0b'
                  }}>
                    <h5 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#d97706',
                      marginBottom: '4px'
                    }}>
                      ⏳ Pending Verification
                    </h5>
                    <p style={{ fontSize: '14px', color: '#d97706', margin: 0 }}>
                      Account created but profile incomplete or under review
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Role and Permission Management */}
            <div style={{ marginTop: '32px' }}>
              <h3 style={{
                fontSize: '20px',
                fontWeight: 'bold',
                color: 'var(--primary-black)',
                marginBottom: '16px'
              }}>
                🔐 Role and Permission Management
              </h3>

              <div style={{ marginBottom: '24px' }}>
                <h4 style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: 'var(--primary-black)',
                  marginBottom: '12px'
                }}>
                  Staff Permissions
                </h4>
                <p style={{ color: 'var(--text-gray)', lineHeight: 1.6, marginBottom: '12px' }}>
                  Grant staff status to GCRA employees for administrative access:
                </p>
                <ul style={{
                  color: 'var(--text-gray)',
                  lineHeight: 1.6,
                  paddingLeft: '20px',
                  marginBottom: '16px'
                }}>
                  <li><strong>Django Admin Access:</strong> Check "Staff status" to enable admin panel access</li>
                  <li><strong>Application Review:</strong> Permission to review and approve student applications</li>
                  <li><strong>Course Management:</strong> Ability to create and modify course information</li>
                  <li><strong>User Management:</strong> Limited user account administration capabilities</li>
                </ul>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <h4 style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: 'var(--primary-black)',
                  marginBottom: '12px'
                }}>
                  Superuser Privileges
                </h4>
                <div style={{
                  padding: '16px',
                  backgroundColor: '#fee2e2',
                  borderRadius: '6px',
                  border: '1px solid #ef4444',
                  marginBottom: '16px'
                }}>
                  <h5 style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#dc2626',
                    marginBottom: '8px'
                  }}>
                    ⚠️ Superuser Access
                  </h5>
                  <p style={{ fontSize: '14px', color: '#dc2626', margin: 0 }}>
                    Grant superuser status only to trusted system administrators. Superusers have
                    complete access to all data and system functions.
                  </p>
                </div>
                <ul style={{
                  color: 'var(--text-gray)',
                  lineHeight: 1.6,
                  paddingLeft: '20px',
                  marginBottom: '16px'
                }}>
                  <li><strong>Full System Access:</strong> Complete control over all platform features</li>
                  <li><strong>User Management:</strong> Create, modify, and delete any user account</li>
                  <li><strong>Data Administration:</strong> Access to all database records and settings</li>
                  <li><strong>System Configuration:</strong> Modify system settings and configurations</li>
                </ul>
              </div>
            </div>

            {/* User Management Best Practices */}
            <div style={{
              padding: '20px',
              backgroundColor: 'var(--background-gray)',
              borderRadius: '8px',
              marginTop: '32px'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: 'bold',
                color: 'var(--primary-black)',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                💡 User Administration Best Practices
              </h3>
              <ul style={{
                color: 'var(--text-gray)',
                lineHeight: 1.6,
                paddingLeft: '20px',
                margin: 0
              }}>
                <li><strong>Regular audits:</strong> Review user accounts and permissions quarterly</li>
                <li><strong>Principle of least privilege:</strong> Grant minimum necessary permissions</li>
                <li><strong>Account deactivation:</strong> Disable rather than delete accounts for audit trail</li>
                <li><strong>Strong authentication:</strong> Enforce secure password policies</li>
                <li><strong>Profile monitoring:</strong> Ensure profile completion for course eligibility</li>
                <li><strong>Duplicate prevention:</strong> Monitor for duplicate ID numbers and email addresses</li>
                <li><strong>Data privacy:</strong> Protect personal information according to POPIA requirements</li>
                <li><strong>Access logging:</strong> Monitor admin access and user activity patterns</li>
                <li><strong>Staff training:</strong> Ensure admin users understand their responsibilities</li>
                <li><strong>Backup procedures:</strong> Regular backups of user data and configurations</li>
              </ul>
            </div>

            </div>
          </div>
        </div>
      </div>
    </HelpLayout>
  );
};

export default UserAdministrationHelp;
